import { IClassNameDisplay, IOnGenerate, Summarize } from '@adaptiva/core/summary';
import { EBuiltInIds, EJavaClassNames, ServerObject } from '@adaptiva/data-service';
import { Observable, of } from 'rxjs';
import { cloudRelayDiscoveryMode } from '../../../summarize/';
import { ICloudRelayServer } from '../../interfaces/';

export class CloudRelayServer extends ServerObject implements ICloudRelayServer, IOnGenerate {

	public static override readonly classNameDisplay: IClassNameDisplay = {
		singular: 'Cloud Relay Server',
		plural: 'Cloud Relay Servers'
	};

	public static readonly className: string = EJavaClassNames.CloudRelayServer;

	// tenant id that has been assigned to this server
	// automatically allocated by the system when the object is first created
	@Summarize({
		type: 'number',
		columnDisplay: 'Tenant ID'
	})
	public tenantId: number;

	// automatically filled out by the system when the object is first created
	// upper 32 bits set to tenant id, lower 32 bits set to 0
	@Summarize({
		type: 'number',
		columnDisplay: 'Globally Unique ID',
		transformers: {
			toDisplay: CloudRelayServer.globallyUniqueIdTransformer
		}
	})
	public globallyUniqueId: BigInt;

	// public key for this server, from its signed certificate issued by Adaptiva
	// automatically generated by the system when the object is first created
	public publicKey: number[];

	// public key encoded as a Base64Url string
	// automatically generated by the system when the object is first created
	public encodedPublicKey: string;

	// Adaptiva server guid
	// generated automatically when the server is installed
	@Summarize({
		type: 'string',
		columnDisplay: 'Server GUID'
	})
	public serverGuid: string;

	// fully qualified domain name or public IP address of the server
	@Summarize({
		type: 'string',
		columnDisplay: 'FQDN'
	})
	public fqdn: string;

	// Offline/maintenance (0): no Adaptiva server or client will connect with this cloud relay server
	// Production (1): customers’ production servers and clients will connect with this server
	// Test (2): Adaptiva’s test servers and clients will connect with this server
	@Summarize({
		type: 'string',
		columnDisplay: 'Discovery Mode',
		isHeading: true,
		transformers: {
			toDisplay: cloudRelayDiscoveryMode
		}
	})
	public discoveryMode: number;

	// true if the server is currently online, else false
	@Summarize({ type: 'boolean' })
	public online: boolean;

	// numeric timestamp of the last heartbeat that was received from the server
	@Summarize({
		type: 'timestamp',
		columnDisplay: 'Last Heartbeat Time',
		isHeading: true
	})
	public lastHeartbeatTime: number;

	@Summarize({
		type: 'number',
		columnDisplay: 'Latitude',
		isHeading: false
	})
	public latitude: float;

	@Summarize({
		type: 'number',
		columnDisplay: 'Longitude',
		isHeading: false
	})
	public longitude: float;

	public parentFolderId: number = EBuiltInIds.ROOT_FOLDER_CLOUD_RELAY_SERVER_OBJECT;

	public javaClassName = EJavaClassNames.CloudRelayServer;

	constructor(relayServer?: object) {
		super(relayServer);
		if (this.globallyUniqueId) {
			this.globallyUniqueId = BigInt(+this.globallyUniqueId);
		}
	}

	public static fromJSON(_json: any): CloudRelayServer {
		const _server = new CloudRelayServer();
		for (const property of Object.keys(_json)) {
			if (_json[property]) {
				if (property === 'globallyUniqueId') {
					_server.globallyUniqueId = BigInt(_json.globallyUniqueId);
				} else {
					_server[property] = _json[property];
				}
			}
		}
		return _server;
	}

	public static globallyUniqueIdTransformer(_server: CloudRelayServer): Observable<string> {
		if (_server.globallyUniqueId) {
			if (+_server.globallyUniqueId >= Number.MAX_SAFE_INTEGER) {
				return of(BigInt(+_server.globallyUniqueId).toString());
			}
			if (typeof _server.globallyUniqueId === 'bigint') {
				return of(_server.globallyUniqueId?.toString());
			}
		}
		return of('--');
	}

	public override toJSON(): this {
		const _serialized: any = Object.assign({}, this);
		// globallyUniqueId is a 64-bit long that actually uses all of its bits
		// so we need to use BigInt and stringify it since JS can't handle that many bits
		if (typeof _serialized.globallyUniqueId?.toString === 'function') {
			_serialized.globallyUniqueId = _serialized.globallyUniqueId.toString();
		}
		return _serialized;
	}

	public onGenerate(): void {
		if (typeof this.globallyUniqueId === 'number') {
			this.globallyUniqueId = BigInt(this.globallyUniqueId || 0);
		}
	}

}

export namespace CloudRelayServer {
	export const enum DiscoveryMode {
		OUT_OF_SERVICE,
		PROD,
		TEST
	}
}
